import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProjects, createProject, updateProject, deleteProject } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { FolderKanban, Plus, Pencil, Trash2, ListChecks } from 'lucide-react';
import { toast } from 'sonner';

const PROJECT_DEPARTMENTS = ['Design & Development', 'Marketing', 'Sales Coordinators'];

export const ProjectsPage = () => {
  const { user, isSuperAdmin, isAdmin, isCoordinator } = useAuth();
  const navigate = useNavigate();
  const canCreate = isSuperAdmin || isAdmin || isCoordinator;

  const myDepts = (user?.departments && user.departments.length) ? user.departments : (user?.department ? [user.department] : []);
  const myProjectDepts = isSuperAdmin ? PROJECT_DEPARTMENTS : myDepts.filter(d => PROJECT_DEPARTMENTS.includes(d));
  const defaultDept = myProjectDepts[0] || PROJECT_DEPARTMENTS[0];
  const emptyForm = { name: '', description: '', department: defaultDept };

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await getProjects();
      setProjects(res.data || []);
    } catch {
      toast.error('Failed to load projects');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || '', department: p.department || defaultDept });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Project name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateProject(editing.id, { name: form.name, description: form.description });
        toast.success('Project updated');
      } else {
        await createProject({ name: form.name, description: form.description, department: form.department });
        toast.success('Project created');
      }
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save project');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteProject(id);
      toast.success('Project deleted');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete project');
    }
  };

  const canEdit = (p) => isSuperAdmin || isAdmin || p.created_by === user?.id;
  const canDelete = (p) => isSuperAdmin || isAdmin || p.created_by === user?.id;

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#F5A623]/15 flex items-center justify-center">
            <FolderKanban className="h-5 w-5 text-[#F5A623]" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold">Projects</h1>
            <p className="text-sm text-muted-foreground">Group and track your department's tasks by project</p>
          </div>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="new-project-btn">
            <Plus className="h-4 w-4 mr-2" /> New Project
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]" />
        </div>
      ) : projects.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-muted-foreground" data-testid="projects-empty">
            <FolderKanban className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>No projects yet.</p>
            {canCreate && <p className="text-sm mt-1">Create one to start grouping your department's tasks.</p>}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="projects-table">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-muted-foreground">
                  <th className="text-left font-medium px-4 py-3">Project</th>
                  <th className="text-left font-medium px-4 py-3">Department</th>
                  <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Created by</th>
                  <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Status</th>
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.id} className="border-b border-border/40 hover:bg-accent/40 transition-colors" data-testid={`project-row-${p.id}`}>
                    <td className="px-4 py-3 align-top">
                      <button onClick={() => navigate(`/tasks?project=${p.id}`)} className="text-left group" data-testid={`project-open-${p.id}`}>
                        <p className="font-semibold text-foreground group-hover:text-[#F5A623] transition-colors">{p.name}</p>
                        {p.description && <p className="text-xs text-muted-foreground line-clamp-1 max-w-xs">{p.description}</p>}
                      </button>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <Badge variant="secondary" className="text-[10px]" data-testid={`project-dept-${p.id}`}>{p.department}</Badge>
                    </td>
                    <td className="px-4 py-3 align-top hidden md:table-cell text-muted-foreground">{p.created_by_name || '—'}</td>
                    <td className="px-4 py-3 align-top hidden sm:table-cell">
                      <Badge variant="outline" className="text-[10px]">{p.status || 'Active'}</Badge>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="text-xs h-8" onClick={() => navigate(`/tasks?project=${p.id}`)} data-testid={`project-tasks-${p.id}`}>
                          <ListChecks className="h-3.5 w-3.5 mr-1.5" /> Tasks
                        </Button>
                        {canCreate && (
                          <Button variant="ghost" size="sm" className="text-xs h-8 text-[#F5A623]" onClick={() => navigate(`/tasks/new?project=${p.id}`)} data-testid={`project-new-task-${p.id}`}>
                            <Plus className="h-3.5 w-3.5 mr-1.5" /> New Task
                          </Button>
                        )}
                        {canEdit(p) && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`project-edit-${p.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete(p) && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`project-delete-${p.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete project?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  "{p.name}" will be removed. Tasks linked to it remain but lose their project association.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={() => handleDelete(p.id)} data-testid={`confirm-delete-${p.id}`}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Project' : 'New Project'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Q3 Brand Launch" data-testid="project-name-input" />
            </div>
            {!editing && (isSuperAdmin || myProjectDepts.length > 1) && (
              <div className="space-y-2">
                <Label>Department *</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger data-testid="project-department-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(isSuperAdmin ? PROJECT_DEPARTMENTS : myProjectDepts).map(d => (
                      <SelectItem key={d} value={d} data-testid={`project-dept-option-${d}`}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What is this project about?" data-testid="project-description-input" />
            </div>
            {editing && (
              <p className="text-xs text-muted-foreground">Department: <span className="font-medium">{editing.department}</span></p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" onClick={handleSave} disabled={saving} data-testid="save-project-btn">
              {saving ? 'Saving...' : editing ? 'Update Project' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsPage;
